import { useDebounceState } from "@prismicio/editor-support/React"
import { getUrlTitle, toURL } from "@prismicio/editor-support/URL"
import {
  Badge,
  Box,
  ErrorBoundary,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
  Popover,
  type TextInputProps,
} from "@prismicio/editor-ui"
import {
  FilledDocumentLinkContent,
  FilledImageLinkContent,
  FilledLinkContent,
  type LinkContent,
} from "@prismicio/types-internal/lib/content"
import type { Link } from "@prismicio/types-internal/lib/customtypes"
import { type PropsWithChildren, Suspense, useState } from "react"

import type { MediaAssetOrExternalImage } from "../../core/MediaLibrary/hooks/mediaLibraryData"
import { useDocumentList } from "../../core/service"
import { throwConfigError, useEditorConfig } from "../../EditorConfig"
import { FieldTextInput } from "../FieldTextInput"
import { MediaDialog } from "../ImageField/MediaDialog"
import { getFieldLabel } from "../utils"
import { DocumentsModal } from "./Documents/DocumentsModal"
import { LinkDescriptionCard } from "./LinkDescriptionCard"
import { type FieldSelectType, LinkFieldActions } from "./LinkFieldActions"
import { LinkImageCard } from "./LinkImageCard"
import { useDocumentTitle, useLinkField } from "./useLinkField"
import { WebLinkModal, type WebLinkOptions } from "./WebLinks/WebLinkModal"

type LinkFieldAppearance = "normal" | "compact" | "condensedButtons"

interface LinkFieldProps {
  id?: string
  field: Link
  content: LinkContent | undefined
  onContentChange: (content: LinkContent | undefined) => void
  appearance?: LinkFieldAppearance
  backgroundColor?: "transparent" | "grey1"
  readOnly: boolean
}

export function LinkField(props: LinkFieldProps) {
  const {
    content,
    field,
    id,
    onContentChange,
    appearance = "normal",
    backgroundColor = "grey1",
    readOnly,
  } = props

  // Debounce because the text input could have the focus very briefly before relinquishing it.
  // For instance, this occurs when clicking on a filled field, which will show the WebLinkModal and steal the focus back.
  const showPopover = useDebounceState({ initialState: false })
  const [openLinkModal, setOpenLinkModal] = useState<FieldSelectType>()

  const {
    contentType,
    fieldValue,
    errorBoundaryRef,
    onDocumentSelected,
    onError,
    onMediaSelected,
    onValueChange,
    selectType,
    onTypeChange,
    isTypeSelectEnabled,
    displayTextEnabled,
    ...textInputProps
  } = useLinkField({ content, field, onContentChange })

  const hasDocumentValue = contentType === "DocumentLink" && fieldValue !== ""
  const isWebLink = contentType === "ExternalLink"
  const validWebLinkUrl = isWebLink && toURL(fieldValue)
  const allowTargetBlank = Boolean(field.config?.allowTargetBlank)

  function onOpenLinkModal() {
    if (readOnly) return
    switch (contentType) {
      case "DocumentLink":
        return setOpenLinkModal("document")
      case "ExternalLink":
        return setOpenLinkModal("web")
      case "FileLink":
      case "ImageLink":
      case "MediaLink":
        return setOpenLinkModal("media")
      default:
        return setOpenLinkModal(undefined)
    }
  }

  const sharedInputProps: LinkFieldTextInputProps = {
    ...textInputProps,
    id,
    onFocus: () => {
      showPopover.set(true)
    },
    onBlur: () => {
      showPopover.set(false)
    },
    onOpenLinkModal,
    onUnlink: () => {
      if (isTypeSelectEnabled) onTypeChange(undefined)
      else onValueChange("")
    },
    onSelectValueChange: (newValue: FieldSelectType) => {
      onTypeChange(newValue)
      setOpenLinkModal(newValue)
    },
    value: fieldValue,
    allowTargetBlank,
    selectType,
    onValueChange,
    isTypeSelectEnabled,
    displayTextEnabled,
    onKeyDown(e) {
      if (displayTextEnabled) return
      if (e.key === "Enter" || e.key === " ") {
        onOpenLinkModal()
        // avoid typing a space in the input in the newly opened modal
        e.preventDefault()
      }
    },
    onClick: () => {
      if (displayTextEnabled || readOnly) return
      onOpenLinkModal()
    },
    appearance,
    readOnly,
  }

  const { searchDocuments } = useEditorConfig()

  function onOpenChange(open: boolean) {
    if (open) return

    // a link is considered selected when 'content' is defined and its not an "AnyLink",
    // as "AnyLink" indicates no specific link has been chosen yet (e.g., web, media, or document).
    const isLinkSelected = content && content.value.__TYPE__ !== "AnyLink"

    // if the modal is closed without selecting anything specific, reset the type.
    // only when it's a specific link (link to media, link to document).
    if (!open && !isLinkSelected && isTypeSelectEnabled) onTypeChange(undefined)

    setOpenLinkModal(undefined)
  }

  function handleWebLinkChange(url: string, options: WebLinkOptions) {
    onValueChange(url, options)
    setOpenLinkModal(undefined)
  }

  function handleMediaChange(media: MediaAssetOrExternalImage) {
    onMediaSelected(media)
    setOpenLinkModal(undefined)
  }

  function handleDocumentChange(documentId: string) {
    onDocumentSelected(documentId)
    setOpenLinkModal(undefined)
  }

  return (
    <>
      <Popover
        align="start"
        sideOffset={-3}
        focusOnEnter={false}
        open={showPopover.debouncedValue}
        anchor={
          <Field
            variant={getFieldAppearance(appearance)}
            color={backgroundColor}
          >
            {hasDocumentValue ? (
              <DocumentFieldIcon
                documentId={fieldValue}
                displayTextEnabled={displayTextEnabled}
              />
            ) : (
              <FieldIcon
                name={getFieldIcon({ selectType, displayTextEnabled })}
              />
            )}
            {appearance !== "compact" && (
              <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
            )}
            <FieldControl>
              {hasDocumentValue && searchDocuments ? (
                <ErrorBoundaryAndSuspenseWrapper
                  errorBoundaryRef={errorBoundaryRef}
                  onError={onError}
                  fallback={
                    <LinkFieldTextInput {...sharedInputProps} value="..." />
                  }
                  renderError={() => renderError(sharedInputProps)}
                >
                  <DocumentContent
                    {...sharedInputProps}
                    documentId={fieldValue}
                  />
                </ErrorBoundaryAndSuspenseWrapper>
              ) : (
                <LinkFieldTextInput {...sharedInputProps} />
              )}
            </FieldControl>
          </Field>
        }
      >
        {validWebLinkUrl && (
          <LinkDescriptionCard
            url={validWebLinkUrl}
            title={getUrlTitle(validWebLinkUrl) ?? ""}
            subTitle={validWebLinkUrl.toString()}
          />
        )}

        {content && FilledImageLinkContent.is(content.value) && (
          <LinkImageCard
            url={content.value.url}
            title={content.value.name}
            subTitle={`${content.value.width} x ${content.value.height} pixels`}
          />
        )}
      </Popover>

      <WebLinkModal
        open={openLinkModal === "web"}
        onOpenChange={onOpenChange}
        onUpdate={handleWebLinkChange}
        url={fieldValue}
        openInNewTab={textInputProps.isTargetNewTab}
        showNewTabButton={Boolean(field.config?.allowTargetBlank)}
      />

      <MediaDialog
        open={openLinkModal === "media"}
        onOpenChange={onOpenChange}
        onMediaSelected={handleMediaChange}
        assetType="all"
      />

      <DocumentsModal
        open={openLinkModal === "document"}
        onOpenChange={onOpenChange}
        onSelectDocument={handleDocumentChange}
        fixedCustomTypes={
          new Set(
            field.config?.customtypes?.map((ct) => {
              return typeof ct === "string" ? ct : ct.id
            }),
          )
        }
        fixedTags={new Set(field.config?.tags)}
        linkedDocumentId={
          content && FilledDocumentLinkContent.is(content.value)
            ? content.value.id
            : undefined
        }
      />
    </>
  )
}

function getFieldAppearance(appearance: LinkFieldAppearance) {
  switch (appearance) {
    case "compact":
      return "compact"
    case "normal":
      return "normal"
    default:
      return undefined
  }
}

function renderError(props: LinkFieldTextInputProps) {
  return <LinkFieldTextInput {...props} />
}

type BaseInputProps = {
  id?: string
  isTypeSelectEnabled: boolean
  selectType?: FieldSelectType
  onFocus: () => void
  onSelectValueChange: (newValue: FieldSelectType) => void
  onOpenLinkModal: () => void
  onUnlink: () => void
  displayTextEnabled: boolean
  displayText: string
  onDisplayTextChange: (newValue: string) => void
  onClick?: TextInputProps["onClick"]
  onKeyDown?: TextInputProps["onKeyDown"]
  appearance?: LinkFieldAppearance
  variants: string[]
  variant?: string
  onVariantChange: (newVariant: string) => void
  readOnly: boolean
}

type LinkFieldTextInputProps = BaseInputProps & {
  placeholder?: string
  value: string
  isTargetNewTab: boolean
  onValueChange: (newValue: string) => void
  onBlur: () => void
  allowTargetBlank: boolean
}

function LinkFieldTextInput(props: LinkFieldTextInputProps) {
  const {
    id,
    value,
    onOpenLinkModal,
    onBlur,
    isTypeSelectEnabled,
    selectType,
    onSelectValueChange,
    onUnlink,
    isTargetNewTab,
    allowTargetBlank,
    displayTextEnabled,
    displayText,
    onFocus,
    onClick,
    onKeyDown,
    appearance,
    placeholder,
    variants,
    variant,
    onVariantChange,
    onDisplayTextChange,
    onValueChange,
    readOnly,
  } = props

  return (
    <Box padding={{ block: 4 }} width="100%">
      <FieldTextInput
        id={id}
        textOverflow="ellipsis"
        variant={displayTextEnabled ? "normal" : "link"}
        readOnly={!displayTextEnabled || readOnly}
        placeholder={displayTextEnabled ? "Add your display text" : placeholder}
        padding={getFieldTextInputPadding(appearance)}
        value={displayTextEnabled ? displayText : value}
        cursor={getCursor({ value, displayTextEnabled, isTypeSelectEnabled })}
        onBlur={onBlur}
        onFocus={onFocus}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onValueChange={displayTextEnabled ? onDisplayTextChange : onValueChange}
        endAdornment={
          <LinkFieldActions
            hasLink={Boolean(value && selectType)}
            onUnlink={onUnlink}
            selectEnabled={isTypeSelectEnabled}
            selectedType={selectType}
            value={value}
            onOpenLinkModal={onOpenLinkModal}
            onValueChange={onSelectValueChange}
            openInNewTab={isTargetNewTab}
            condensedButtons={appearance === "condensedButtons"}
            showNewTabButton={allowTargetBlank && selectType === "web"}
            displayTextEnabled={displayTextEnabled}
            variants={variants}
            variant={variant}
            onVariantChange={onVariantChange}
            readOnly={readOnly}
          />
        }
      />
    </Box>
  )
}

type DocumentContentProps = BaseInputProps & {
  documentId: string
}

function DocumentContent(props: DocumentContentProps) {
  const {
    id,
    documentId,
    onOpenLinkModal,
    isTypeSelectEnabled,
    selectType,
    displayTextEnabled,
    onSelectValueChange,
    onUnlink,
    onFocus,
    onClick,
    onKeyDown,
    appearance,
    variants,
    variant,
    displayText,
    onVariantChange,
    onDisplayTextChange,
    readOnly,
  } = props

  const documentTitle = useDocumentTitle(documentId)
  const isBrokenLink = useIsBrokenLink(documentId)

  return (
    <Box padding={{ block: 4 }} width="100%">
      <FieldTextInput
        id={id}
        textOverflow="ellipsis"
        padding={getFieldTextInputPadding(appearance)}
        placeholder={displayTextEnabled ? "Add your display text" : undefined}
        readOnly={!displayTextEnabled || readOnly}
        value={displayTextEnabled ? displayText : documentTitle}
        variant={displayTextEnabled ? "normal" : "link"}
        cursor={getCursor({
          value: documentTitle,
          displayTextEnabled,
          isTypeSelectEnabled,
        })}
        onFocus={onFocus}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onValueChange={displayTextEnabled ? onDisplayTextChange : undefined}
        endAdornment={
          <Box gap={4}>
            {isBrokenLink && <Badge title="Broken Link" color="tomato" />}
            <LinkFieldActions
              hasLink
              onUnlink={onUnlink}
              selectEnabled={isTypeSelectEnabled}
              selectedType={selectType}
              value={documentTitle}
              onOpenLinkModal={onOpenLinkModal}
              condensedButtons={appearance === "condensedButtons"}
              onValueChange={onSelectValueChange}
              displayTextEnabled={displayTextEnabled}
              variants={variants}
              variant={variant}
              onVariantChange={onVariantChange}
              readOnly={readOnly}
            />
          </Box>
        }
      />
    </Box>
  )
}

interface DocumentFieldIconProps {
  documentId: string
  displayTextEnabled: boolean
}

function DocumentFieldIcon(props: DocumentFieldIconProps) {
  const { documentId, displayTextEnabled } = props
  return (
    <ErrorBoundary renderError={() => <FieldIcon name="warning" />}>
      <Suspense
        fallback={
          <FieldIcon
            name={getFieldIcon({ selectType: "document", displayTextEnabled })}
          />
        }
      >
        <DocumentFieldIconContent
          documentId={documentId}
          displayTextEnabled={displayTextEnabled}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

function DocumentFieldIconContent(props: DocumentFieldIconProps) {
  const { displayTextEnabled, documentId } = props

  const isBrokenLink = useIsBrokenLink(documentId)

  if (isBrokenLink) return <FieldIcon name="linkOff" />

  return (
    <FieldIcon
      name={getFieldIcon({ selectType: "document", displayTextEnabled })}
    />
  )
}

function getFieldTextInputPadding(appearance: LinkFieldAppearance | undefined) {
  switch (appearance) {
    case "compact":
      return { input: { inline: 4 } } as const
    default:
      return undefined
  }
}

interface ErrorBoundaryAndSuspenseWrapperProps extends PropsWithChildren {
  errorBoundaryRef: React.RefObject<ErrorBoundary>
  onError: VoidFunction
  renderError: () => React.ReactNode
  fallback: React.ReactNode
}

function ErrorBoundaryAndSuspenseWrapper(
  props: ErrorBoundaryAndSuspenseWrapperProps,
) {
  const { errorBoundaryRef, onError, children, fallback, renderError } = props

  return (
    <ErrorBoundary
      ref={errorBoundaryRef}
      onError={onError}
      renderError={renderError}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}

interface GetFieldIconArgs {
  selectType?: FieldSelectType
  displayTextEnabled?: boolean
}

export function getFieldIcon(args: GetFieldIconArgs) {
  const { selectType, displayTextEnabled } = args

  if (displayTextEnabled) return "notes"
  switch (selectType) {
    case "document":
      return "description"
    case "web":
      return "public"
    case "media":
      return "image"
    default:
      return "link"
  }
}

interface LinkFieldActionsArgs {
  value: string
  displayTextEnabled: boolean
  isTypeSelectEnabled: boolean
}

function getCursor(args: LinkFieldActionsArgs) {
  const { value, displayTextEnabled, isTypeSelectEnabled } = args

  if (displayTextEnabled) return "text" as const
  if (isTypeSelectEnabled && !value) return "default" as const
  return "pointer" as const
}

/*
 * Similar to the LinkField component, but ensuring the content is always filled.
 * It also ensures that the display text can't be used.
 */

interface FilledLinkFieldProps {
  id?: string
  appearance?: LinkFieldAppearance
  field: Link & { config?: { allowText?: false } }
  content: FilledLinkContent | undefined
  onContentChange: (content: FilledLinkContent | undefined) => void
  readOnly: boolean
}

export function FilledLinkField(props: FilledLinkFieldProps) {
  const { onContentChange: onFilledContentChange, ...otherProps } = props

  function onContentChange(content: LinkContent | undefined) {
    const filledContent = FilledLinkContent.is(content) ? content : undefined
    onFilledContentChange(filledContent)
  }

  return <LinkField {...otherProps} onContentChange={onContentChange} />
}

function useIsBrokenLink(documentId: string) {
  const config = useEditorConfig()

  if (!config.uidField) throwConfigError("uidField")

  const { coreApiBaseUrl, authStrategy } = config

  const { data } = useDocumentList({
    baseUrl: coreApiBaseUrl,
    authStrategy,
    documentIds: [documentId],
    statuses: ["archived", "unclassified", "published"],
  })

  if (data.results.length === 0 || data.results.length > 1) return false

  const document = data.results[0]

  return document?.versions.every((version) => version.status === "archived")
}
